<template>
  <div class="mt-2 panel-compact">
    <template v-if="!enPosicionFirma">
      <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2">
        <div>
          <h5 class="mb-1 d-flex align-items-center gap-2 section-title">
            <i class="fas fa-user-check"></i>
            Firmar documento
          </h5>
          <small class="text-muted">Selecciona usuarios para firmar este documento</small>
        </div>
        <div class="d-flex gap-2 mt-2 mt-sm-0">
          <button class="btn btn-primary btn-sm" @click="mostrarModal = true" :disabled="cargandoFirmantes">
            <i class="fas fa-user-plus me-1"></i>
            Añadir firmantes
          </button>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="small text-muted">Seleccionados: {{ firmantesSeleccionados.length }}</div>
      </div>

      <div class="row g-2">
        <div class="col-12">
          <div class="card shadow-sm selected-card">
            <div class="card-body py-3">
              <div class="d-flex align-items-center gap-2 mb-2">
                <i class="fas fa-list-check text-primary"></i>
                <h6 class="mb-0">Firmantes seleccionados</h6>
              </div>
              <div v-if="firmantesSeleccionados.length === 0" class="text-muted small">
                Aún no has seleccionado firmantes.
              </div>
              <ul class="list-unstyled mb-0 selected-scroll">
                <li v-for="firmante in firmantesSeleccionados" :key="`sel-${firmante.id}`"
                  class="d-flex align-items-center justify-content-between border-bottom py-2">
                  <div class="me-2">
                    <strong class="text-truncate d-block">{{ firmante.name || firmante.username }}</strong>
                    <div class="text-muted small text-truncate">{{ firmante.email || 'Sin email' }}</div>
                  </div>
                  <button class="btn btn-sm btn-outline-danger" @click="alternarFirmante(firmante)">
                    <i class="fas fa-times"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <button class="btn btn-primary" :disabled="firmantesSeleccionados.length === 0" @click="continuarFirma">
          Continuar
        </button>
      </div>
    </template>

    <template v-else-if="!enResumen">
      <SelectorPosicionFirma
        ref="selectorPosicionRef"
        :firmantes="posicionesFirmantes"
        :registro-seleccionado="registroSeleccionado"
        :esquema-id="id || esquema?.id"
        :show-notification="showNotification"
        @posicion-guardada="onPosicionGuardada"
        @posiciones-limpias="onPosicionesLimpias"
      />
      
      <div class="d-flex justify-content-between mt-3">
        <button class="btn btn-secondary" @click="volverASeleccion">
          <i class="fas fa-arrow-left me-2"></i>
          Volver
        </button>
        <button class="btn btn-primary" :disabled="!todasPosicionesListas" @click="mostrarResumen">
          <i class="fas fa-save me-2"></i>
          Guardar posiciones
        </button>
      </div>
    </template>

    <!-- Vista de Resumen -->
    <template v-else>
      <div class="card shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex align-items-center gap-2 mb-3">
            <i class="fas fa-clipboard-check text-primary"></i>
            <h5 class="mb-0">Resumen de firmantes</h5>
          </div>
          
          <div class="alert alert-light border mb-4">
            <i class="fas fa-info-circle me-2"></i>
            <strong>¿Estás seguro de enviar este documento a firmar?</strong>
            <p class="mb-0 mt-2">Se enviará el documento a los siguientes usuarios para su firma:</p>
          </div>

          <div class="list-group">
            <div v-for="(item, index) in posicionesResumen" 
              :key="`resumen-${item.firmante.id}-${index}`"
              class="list-group-item d-flex align-items-center gap-3">
              <div class="d-flex align-items-center gap-2 flex-grow-1">
                <div class="rounded-circle d-flex align-items-center justify-content-center" 
                  :style="{ 
                    width: '40px', 
                    height: '40px', 
                    backgroundColor: colores[index % colores.length],
                    color: 'white',
                    fontWeight: 'bold'
                  }">
                  {{ index + 1 }}
                </div>
                <div class="flex-grow-1">
                  <strong>{{ item.firmante.name || item.firmante.username }}</strong>
                  <div class="text-muted small">
                    <i class="fas fa-envelope me-1"></i>{{ item.firmante.email || 'Sin email' }}
                  </div>
                  <div class="text-muted small mt-1">
                    <i class="fas fa-file-pdf me-1"></i>
                    Página {{ item.posicion?.pagina || 'N/A' }}
                  </div>
                </div>
              </div>
              <i class="fas fa-check-circle text-success" style="font-size: 24px;"></i>
            </div>
          </div>

          <div class="card mt-3">
            <div class="card-body">
              <div class="mb-3">
                <label class="form-label small text-muted">Fecha límite (opcional)</label>
                <input type="datetime-local" class="form-control" v-model="fechaLimite" :disabled="enviando">
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between mt-4">
            <button class="btn btn-secondary" @click="volverAPosiciones" :disabled="enviando">
              <i class="fas fa-arrow-left me-2"></i>
              Volver a posiciones
            </button>
            <button class="btn btn-success" @click="confirmarEnvio" :disabled="enviando">
              <span v-if="enviando" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <i class="fas fa-paper-plane me-2"></i>
              Confirmar y enviar a firmar
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal para seleccionar contactos - fuera de los templates condicionales -->
    <div class="modal fade" :class="{ show: mostrarModal, 'd-block': mostrarModal }" tabindex="-1" style="z-index: 1050; display: none;" v-show="mostrarModal">
      <div class="modal-dialog modal-xl mt-7">
        <div class="modal-content">
          
          <div class="modal-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div class="input-group" style="max-width: 400px;">
                <span class="input-group-text"><i class="fas fa-search"></i></span>
                <input type="text" class="form-control" v-model="busquedaFirmantes" placeholder="Buscar usuario...">
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-secondary btn-sm" @click="recargarUsuarios">
                  <i class="fas fa-sync-alt me-1"></i>
                  Recargar
                </button>
              </div>
            </div>
            <div class="list-group" style="max-height: 400px; overflow-y: auto;">
              <a v-for="(usuario, index) in firmantesFiltrados" 
                :key="`usuario-modal-${usuario.id}-${index}-${firmantesSeleccionadosIds.length}`"
                href="#" 
                class="list-group-item list-group-item-action"
                :class="{ 
                  'active': firmantesSeleccionadosIds.includes(usuario.id),
                  'bg-primary': firmantesSeleccionadosIds.includes(usuario.id),
                  'text-white': firmantesSeleccionadosIds.includes(usuario.id)
                }"
                @click.prevent="alternarFirmante(usuario)"
                style="border-left: 4px solid transparent; transition: all 0.2s ease;">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <strong>{{ usuario.name || usuario.username }}</strong>
                      <i v-if="firmantesSeleccionadosIds.includes(usuario.id)" class="fas fa-check-circle" :class="firmantesSeleccionadosIds.includes(usuario.id) ? 'text-white' : 'text-success'"></i>
                    </div>
                    <div :class="firmantesSeleccionadosIds.includes(usuario.id) ? 'text-white-50' : 'text-muted'" class="small">
                      <i class="fas fa-user me-1"></i>{{ usuario.username || 'Sin usuario' }}
                    </div>
                    <div :class="firmantesSeleccionadosIds.includes(usuario.id) ? 'text-white-50' : 'text-muted'" class="small">
                      <i class="fas fa-envelope me-1"></i>{{ usuario.email || 'Sin email' }}
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cerrarModal">
              <i class="fas fa-times me-1"></i>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="mostrarModal" class="modal-backdrop fade show" style="z-index: 1040;"></div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, onMounted, nextTick, shallowRef } from 'vue'
import axiosInstance from '@/axios'
import SelectorPosicionFirma from '@/components/SelectorPosicionFirma.vue'

// defineProps y defineEmits son macros de compilación de Vue 3, no necesitan importarse
const props = defineProps({
  esquema: { type: Object, required: true },
  registroSeleccionado: { type: Object, required: true },
  showNotification: { type: Function, required: true },
  id: { type: [String, Number], required: false }
})

const emit = defineEmits(['actualizar-registro', 'continuar-firmas', 'guardar-posiciones'])

// Estados principales
const usuariosDisponibles = ref([])
const firmantesSeleccionados = ref([])
const busquedaFirmantes = ref('')
const cargandoFirmantes = ref(false)
const mostrarModal = ref(false)
const enPosicionFirma = ref(false)
const enResumen = ref(false)
const posicionesResumen = ref([])
const fechaLimite = ref('')
const enviando = ref(false)

// Colores para los marcadores (mismo que en SelectorPosicionFirma)
const colores = [
  "#74b9ff", "#a29bfe", "#55efc4", "#81ecec", "#ff9ff3",
  "#fd79a8", "#ff7675", "#b2bec3", "#fab1a0", "#5352ed"
]

// Estados de selección
const posicionesFirmantes = shallowRef([])
const selectorPosicionRef = ref(null)

// Computed
const firmantesSeleccionadosIds = computed(() => firmantesSeleccionados.value.map(f => f.id))

const firmantesFiltrados = computed(() => {
  const term = busquedaFirmantes.value.toLowerCase().trim()
  const lista = Array.isArray(usuariosDisponibles.value) ? usuariosDisponibles.value : []
  if (!term) return lista
  return lista.filter(usuario => {
    const nombre = `${usuario.name || ''} ${usuario.username || ''}`.toLowerCase()
    const email = (usuario.email || '').toLowerCase()
    return nombre.includes(term) || email.includes(term)
  })
})

const todasPosicionesListas = computed(() => {
  if (!selectorPosicionRef.value) return false
  return selectorPosicionRef.value.todasPosicionesListas || false
})


// Funciones de usuarios
const sincronizarFirmantesConRegistro = () => {
  const seleccionado = props.registroSeleccionado?.value || props.registroSeleccionado
  const ids = Array.isArray(seleccionado?.firmantes) ? seleccionado.firmantes : []
  if (!Array.isArray(usuariosDisponibles.value)) {
    firmantesSeleccionados.value = []
    return
  }
  firmantesSeleccionados.value = usuariosDisponibles.value.filter(u => ids.includes(u.id))
}

const cargarUsuarios = async () => {
  cargandoFirmantes.value = true
  try {
    const response = await axiosInstance.get('/api/usuarios')
    usuariosDisponibles.value = response.data?.users || []
    sincronizarFirmantesConRegistro()
  } catch (error) {
    console.error('Error al cargar usuarios:', error)
    props.showNotification('Error al cargar los usuarios', 'alert-danger')
  } finally {
    cargandoFirmantes.value = false
  }
}

const alternarFirmante = (usuario) => {
  if (!usuario || !usuario.id) return
  
  try {
    const idx = firmantesSeleccionados.value.findIndex(f => f.id === usuario.id)
    if (idx >= 0) {
      // Crear una nueva copia del array en lugar de usar splice directamente
      firmantesSeleccionados.value = firmantesSeleccionados.value.filter(f => f.id !== usuario.id)
    } else {
      // Crear una nueva copia del array en lugar de usar push directamente
      firmantesSeleccionados.value = [...firmantesSeleccionados.value, usuario]
    }
    
    // Diferir el emit para evitar conflictos
    setTimeout(() => {
      try {
        const seleccionado = props.registroSeleccionado?.value || props.registroSeleccionado
        const firmantesIds = firmantesSeleccionados.value.map(f => f.id)
        emit('actualizar-registro', { ...seleccionado, firmantes: firmantesIds })
      } catch (error) {
        console.warn('[alternarFirmante] Error al emitir (ignorado):', error.message)
      }
    }, 0)
  } catch (error) {
    console.warn('[alternarFirmante] Error al alternar firmante (ignorado):', error.message)
  }
}

const cerrarModal = () => {
  mostrarModal.value = false
}

const recargarUsuarios = async () => {
  busquedaFirmantes.value = ''
  await cargarUsuarios()
}

// Las funciones del PDF están ahora en el componente SelectorPosicionFirma

// Funciones de selección
const continuarFirma = async () => {
  const firmantesIds = firmantesSeleccionados.value.map(f => f.id)
  
  // Inicializar array de posiciones
  posicionesFirmantes.value = firmantesSeleccionados.value.map((f, idx) => ({
    firmante: f,
    posicion: null,
    ordenFirma: idx + 1
  }))
  
  enPosicionFirma.value = true
  await nextTick()
  // El componente hijo cargará el PDF automáticamente cuando reciba los firmantes
  emit('continuar-firmas', { firmantes: firmantesSeleccionados.value, firmantesIds })
}

const volverASeleccion = () => {
  enPosicionFirma.value = false
}

// Las funciones de selección de firmantes están ahora en el componente SelectorPosicionFirma

const mostrarResumen = () => {
  if (!todasPosicionesListas.value || !selectorPosicionRef.value) return
  
  // Obtener las posiciones del componente hijo
  posicionesResumen.value = selectorPosicionRef.value.obtenerPosiciones()
  
  enResumen.value = true
}

const volverAPosiciones = () => {
  enResumen.value = false
}

const confirmarEnvio = async () => {
  if (posicionesResumen.value.length === 0 || enviando.value) return
  
  const seleccionado = props.registroSeleccionado?.value || props.registroSeleccionado
  if (!seleccionado?.id) {
    props.showNotification('No hay documento seleccionado', 'alert-warning')
    return
  }
  
  enviando.value = true
  
  try {
    const esquemaId = props.id || props.esquema?.id
    if (!esquemaId) {
      throw new Error('No se pudo obtener el ID del esquema')
    }
    
    // Construir el array de IDs de usuarios
    const usuariosIds = posicionesResumen.value.map(item => item.firmante.id)
    
    // Construir el objeto de posiciones_firma con el formato esperado
    const posicionesFirma = {}
    posicionesResumen.value.forEach(item => {
      if (item.posicion && item.posicion.pagina) {
        const usuarioId = String(item.firmante.id)
        // Usar valores en milímetros (requeridos por el backend)
        posicionesFirma[usuarioId] = {
          pagina: item.posicion.pagina,
          x: item.posicion.xMm ?? 0,
          y: item.posicion.yMm ?? 0,
          ancho: item.posicion.anchoMm ?? 40,
          alto: item.posicion.altoMm ?? 20
        }
      }
    })
    
    // Validar que todas las posiciones tengan valores válidos
    if (Object.keys(posicionesFirma).length === 0) {
      throw new Error('Debe definir al menos una posición de firma')
    }
    
    // Construir el payload
    const payload = {
      metodo_firma: 'pantalla',
      usuarios_ids: usuariosIds,
      posiciones_firma: posicionesFirma
    }
    
    // Añadir fecha_limite solo si está definida
    if (fechaLimite.value) {
      // Convertir datetime-local a formato ISO
      const fecha = new Date(fechaLimite.value)
      payload.fecha_limite = fecha.toISOString()
    }
    
    // Llamar al endpoint
    const response = await axiosInstance.post(`/api/firmarDocumento/${esquemaId}/${seleccionado.id}`, payload)
    
    if (response.data?.success) {
      props.showNotification('Documento enviado a firmar correctamente', 'alert-success')
      
      // Resetear el estado y volver a la vista inicial
      firmantesSeleccionados.value = []
      posicionesResumen.value = []
      enResumen.value = false
      enPosicionFirma.value = false
      fechaLimite.value = ''
      
      // Opcional: emitir evento para actualizar el registro
      emit('guardar-posiciones', posicionesResumen.value)
    } else {
      throw new Error(response.data?.error || 'Error al enviar el documento')
    }
  } catch (error) {
    console.error('Error al enviar documento a firmar:', error)
    const msg = error?.response?.data?.error || error?.message || 'No se pudo enviar el documento a firmar'
    props.showNotification(msg, 'alert-danger')
  } finally {
    enviando.value = false
  }
}

// Handlers de eventos del componente hijo
const onPosicionGuardada = () => {
  // La posición ya está guardada en el componente hijo
  // Solo necesitamos actualizar el estado si es necesario
}

const onPosicionesLimpias = () => {
  // Las posiciones ya están limpiadas en el componente hijo
}

// Las funciones de renderizado y eventos del mouse están ahora en el componente SelectorPosicionFirma

// Lifecycle
onMounted(() => {
  cargarUsuarios()
})
</script>

<style scoped>
.firmante-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.selected-scroll {
  max-height: 300px;
  overflow-y: auto;
}

.pdf-container {
  display: inline-block;
  position: relative;
}
</style>
